import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { TASK_ROW_HEIGHT } from "@/common/constants";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Textarea } from "../ui/textarea";

interface TextSelectProps {
    value: string | null;
    onChange: (value: string) => void;
    trigger: (content: React.ReactNode, value: string | null | undefined) => React.ReactElement;
}

interface TextFormData {
    text: string;
}

export function TextSelect({ value, onChange, trigger }: TextSelectProps) {
    const [open, setOpen] = useState(false);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const { register, handleSubmit, reset, setValue } = useForm<TextFormData>({
        defaultValues: {
            text: value || "",
        },
    });

    const onSubmit = (data: TextFormData) => {
        const textValue = data.text || "";
        onChange(textValue);
        setOpen(false);
    };

    const handleCancel = () => {
        reset({ text: value || "" });
        setOpen(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        e.stopPropagation();
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(onSubmit)();
        } else if (e.key === "Escape") {
            e.preventDefault();
            handleCancel();
        }
    };

    const handleOpenChange = (newOpen: boolean) => {
        if (newOpen) {
            setValue("text", value || "");
            // Use setTimeout to ensure the input is fully rendered and focused
            setTimeout(() => {
                inputRef.current?.select();
            }, 0);
        }
        setOpen(newOpen);
    };

    return (
        <Popover open={open} onOpenChange={handleOpenChange} modal={false}>
            <PopoverTrigger render={trigger(value, value)} />
            <PopoverContent sideOffset={-TASK_ROW_HEIGHT} align="end" className="w-full p-0 max-w-[450px] min-w-64">
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Textarea
                        {...register("text")}
                        ref={(e) => {
                            register("text").ref(e);
                            inputRef.current = e;
                        }}
                        onKeyDown={handleKeyDown}
                        onBlur={handleSubmit(onSubmit)}
                        autoFocus
                    />
                </form>
            </PopoverContent>
        </Popover>
    );
}
